import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Slider,
  Button,
  Chip,
  Autocomplete,
  Switch,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { ExpandMore, LocationOn, Clear } from '@mui/icons-material';
import { setFilters, clearFilters, fetchMissions } from '../redux/slices/missionsSlice';
import './FiltersPanel.css';

const FiltersPanel = ({ onClose }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector(state => state.missions);
  
  const [localFilters, setLocalFilters] = useState({
    disciplines: [],
    location: '',
    distance: 30,
    missionTypes: [],
    rateRange: [0, 100],
    dateRange: '',
    useGeolocation: false,
    ...filters
  });
  
  const disciplines = [
    'Animation', 'Arbitrage', 'Athlétisme', 'Basketball', 'Coaching',
    'Cyclisme', 'Fitness', 'Football', 'Handball', 'Kinésithérapie',
    'Natation', 'Rugby', 'Tennis', 'Volleyball'
  ];
  
  const missionTypes = [
    'Ponctuelle', 'Long terme', 'Week-end', 'Soirée', 'Vacances scolaires'
  ];
  
  const cities = [
    'Paris', 'Lyon', 'Marseille', 'Toulouse', 'Nice', 'Nantes',
    'Strasbourg', 'Montpellier', 'Bordeaux', 'Lille', 'Rennes', 'Reims'
  ];
  
  const handleDisciplineChange = (discipline) => {
    const newDisciplines = localFilters.disciplines.includes(discipline)
      ? localFilters.disciplines.filter(d => d !== discipline)
      : [...localFilters.disciplines, discipline];
    
    setLocalFilters(prev => ({ ...prev, disciplines: newDisciplines }));
  };
  
  const handleMissionTypeChange = (type) => {
    const newTypes = localFilters.missionTypes.includes(type)
      ? localFilters.missionTypes.filter(t => t !== type)
      : [...localFilters.missionTypes, type];
    
    setLocalFilters(prev => ({ ...prev, missionTypes: newTypes }));
  };
  
  const handleRateRangeChange = (event, newValue) => {
    setLocalFilters(prev => ({ ...prev, rateRange: newValue }));
  };
  
  const handleGeolocationToggle = () => {
    if (!localFilters.useGeolocation) {
      // Simulate geolocation
      setLocalFilters(prev => ({
        ...prev,
        useGeolocation: true,
        location: 'Ma position actuelle',
        distance: 10
      }));
    } else {
      setLocalFilters(prev => ({
        ...prev,
        useGeolocation: false,
        location: '',
        distance: 30
      }));
    }
  };
  
  const handleApplyFilters = () => {
    dispatch(setFilters(localFilters));
    dispatch(fetchMissions());
    if (onClose) onClose();
  };
  
  const resetFilters = () => {
    const resetState = {
      disciplines: [],
      location: '',
      distance: 30,
      missionTypes: [],
      rateRange: [0, 100],
      dateRange: '',
      useGeolocation: false
    };
    setLocalFilters(resetState);
    dispatch(clearFilters());
  };
  
  return (
    <Box className="filters-panel">
      <Box className="filters-header">
        <Typography variant="h6" className="filters-title">
          Filtres
        </Typography>
        <Button
          size="small"
          onClick={resetFilters}
          startIcon={<Clear />}
          className="clear-filters-btn"
        >
          Effacer
        </Button>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      {/* Geolocation */}
      <Box className="filter-section">
        <FormControlLabel
          control={
            <Switch
              checked={localFilters.useGeolocation}
              onChange={handleGeolocationToggle}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOn fontSize="small" />
              <Typography variant="body2">Près de moi</Typography>
            </Box>
          }
        />
      </Box>
      
      {/* Location */}
      <Accordion defaultExpanded className="filter-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="medium">
            Localisation
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box className="filter-content">
            <Autocomplete
              options={cities}
              value={localFilters.location}
              onChange={(event, newValue) => {
                setLocalFilters(prev => ({ ...prev, location: newValue || '' }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Ville"
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={localFilters.useGeolocation}
                />
              )}
              freeSolo
            />
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Distance: {localFilters.distance} km
              </Typography>
              <Slider
                value={localFilters.distance}
                onChange={(event, newValue) => {
                  setLocalFilters(prev => ({ ...prev, distance: newValue }));
                }}
                min={5}
                max={100}
                step={5}
                marks={[
                  { value: 10, label: '10km' },
                  { value: 30, label: '30km' },
                  { value: 50, label: '50km' }
                ]}
                valueLabelDisplay="auto"
                className="distance-slider"
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Disciplines */}
      <Accordion defaultExpanded className="filter-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="medium">
            Disciplines
            {localFilters.disciplines.length > 0 && (
              <Chip
                label={localFilters.disciplines.length}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box className="filter-content">
            <Box className="disciplines-grid">
              {disciplines.map((discipline) => (
                <FormControlLabel
                  key={discipline}
                  control={
                    <Checkbox
                      checked={localFilters.disciplines.includes(discipline)}
                      onChange={() => handleDisciplineChange(discipline)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{discipline}</Typography>}
                  className="discipline-checkbox"
                />
              ))}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Mission Types */}
      <Accordion className="filter-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="medium">
            Type de mission
            {localFilters.missionTypes.length > 0 && (
              <Chip
                label={localFilters.missionTypes.length}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box className="filter-content">
            <FormGroup>
              {missionTypes.map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={localFilters.missionTypes.includes(type)}
                      onChange={() => handleMissionTypeChange(type)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{type}</Typography>}
                />
              ))}
            </FormGroup>
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Rate Range */}
      <Accordion className="filter-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="medium">
            Tarif (€/h)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box className="filter-content">
            <Typography variant="body2" gutterBottom>
              {localFilters.rateRange[0]}€ - {localFilters.rateRange[1]}€
            </Typography>
            <Slider
              value={localFilters.rateRange}
              onChange={handleRateRangeChange}
              min={0}
              max={150}
              step={5}
              marks={[
                { value: 0, label: '0€' },
                { value: 50, label: '50€' },
                { value: 100, label: '100€' },
                { value: 150, label: '150€' }
              ]}
              valueLabelDisplay="auto"
              className="rate-slider"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Date Range */}
      <Accordion className="filter-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="medium">
            Disponibilité
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box className="filter-content">
            <TextField
              type="date"
              label="Date de début"
              value={localFilters.dateRange}
              onChange={(e) => {
                setLocalFilters(prev => ({ ...prev, dateRange: e.target.value }));
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* Apply Button */}
      <Box className="filters-actions" sx={{ mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleApplyFilters}
          fullWidth
          className="apply-filters-btn"
          size="large"
        >
          Appliquer les filtres
        </Button>
      </Box>
    </Box>
  );
};

export default FiltersPanel;